import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import AppContext from '../../../Context/AppContext';
import requestAxios from '../../../util/requestAxios';
import AchievementMark from '../Achievements/AchievementMark';
import {
    achievementFamily,
    achievementRequirement,
    nextJourneyAchievement,
    parseAchievementDate,
    recentlyEarned,
} from '../Achievements/achievementVisual';
import './AchievementsPanel.css';

const TABS = [
    { id: 'badges', label: 'Badges' },
    { id: 'milestones', label: 'Milestones' },
    { id: 'streaks', label: 'Streaks' },
];

const emptyGroups = () => ({
    badges: [],
    milestones: [],
    streaks: [],
});

function earnedOf(list) {
    return (list || []).filter((item) => item?.achieved).length;
}

/**
 * V2 Profile achievements catalog — same Customer APIs as classic Achievements modal.
 * Does not modify V1 UserInfo / Achievements.js.
 */
const AchievementsPanel = ({
    active = true,
    goalsCompleted = 0,
    progress = null,
    onEarnedCount,
}) => {
    const { checkUserSession } = useContext(AppContext);
    const { token, userId } = checkUserSession();

    const [tab, setTab] = useState('badges');
    const [showAll, setShowAll] = useState(false);
    const [groups, setGroups] = useState(emptyGroups);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');
    const [selected, setSelected] = useState(null);
    const [recentPreview, setRecentPreview] = useState(null);
    const [isNarrow, setIsNarrow] = useState(() =>
        typeof window !== 'undefined'
            ? window.matchMedia('(max-width: 767px)').matches
            : false
    );

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const mq = window.matchMedia('(max-width: 767px)');
        const onChange = (e) => setIsNarrow(e.matches);
        setIsNarrow(mq.matches);
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else mq.addListener(onChange);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onChange);
            else mq.removeListener(onChange);
        };
    }, []);

    const loadAchievements = useCallback(async () => {
        if (!token || !userId) return;
        setLoading(true);
        setErrorMsg('');
        const { error, data } = await requestAxios({
            url: `Customer/GetCustomerProfileInformation/${userId}`,
            token,
        });
        if (!error && data?.achievements) {
            setGroups({
                badges: data.achievements.badges || [],
                milestones: data.achievements.milestones || [],
                streaks: data.achievements.streaks || [],
            });
        } else {
            setErrorMsg('Could not load achievements.');
        }
        setLoading(false);
    }, [token, userId]);

    useEffect(() => {
        if (!active) return;
        loadAchievements();
    }, [active, loadAchievements]);

    const list = useMemo(() => {
        const raw = groups[tab] || [];
        if (showAll) return raw;
        return raw.filter((item) => item?.achieved);
    }, [groups, tab, showAll]);
    const recent = useMemo(() => recentlyEarned(groups, 7), [groups]);

    useEffect(() => {
        if (!isNarrow) {
            setRecentPreview(null);
            return;
        }
        if (!recent.length) {
            setRecentPreview(null);
            return;
        }
        setRecentPreview((prev) => {
            if (
                prev &&
                recent.some(
                    (item) =>
                        (item.id ?? null) === (prev.id ?? null) &&
                        item.achievementName === prev.achievementName
                )
            ) {
                return prev;
            }
            return recent[0];
        });
    }, [isNarrow, recent]);

    const stats = useMemo(
        () => [
            {
                id: 'badges',
                label: 'Badges',
                earned: earnedOf(groups.badges),
                total: groups.badges.length,
            },
            {
                id: 'milestones',
                label: 'Milestones',
                earned: earnedOf(groups.milestones),
                total: groups.milestones.length,
            },
            {
                id: 'streaks',
                label: 'Streaks',
                earned: earnedOf(groups.streaks),
                total: groups.streaks.length,
            },
        ],
        [groups]
    );

    const earnedTotal = stats.reduce((sum, s) => sum + s.earned, 0);
    const nextGoal = useMemo(
        () => nextJourneyAchievement(groups, progress),
        [groups, progress]
    );

    useEffect(() => {
        if (typeof onEarnedCount === 'function' && !loading && !errorMsg) {
            onEarnedCount(earnedTotal);
        }
    }, [earnedTotal, loading, errorMsg, onEarnedCount]);
    const closeDetail = () => {
        setSelected(null);
    };

    const selectCategory = (id, all = false) => {
        setTab(id);
        setShowAll(Boolean(all));
        setSelected(null);
    };

    const renderCard = (item, index, compact = false) => {
        if (!item) return null;
        const achieved = Boolean(item.achieved);
        const family = achievementFamily(item, compact ? undefined : tab);
        const req = achievementRequirement(item, family);
        return (
            <li key={`${family}-${item.id ?? index}`}>
                <button
                    type="button"
                    className={`dv2-ach__card${
                        achieved ? ' is-earned' : ' is-locked'
                    }${compact ? ' is-recent' : ''}`}
                    onClick={() => setSelected(item)}
                    aria-label={`${item.achievementName || 'Achievement'}${
                        achieved ? '' : `, locked. ${req}`
                    }`}
                >
                    <AchievementMark
                        item={item}
                        tabHint={family}
                        goalsCompleted={
                            item.achievementValue || goalsCompleted
                        }
                        size={compact ? 64 : 78}
                        earned={achieved}
                    />
                    <span className="dv2-ach__copy">
                        <span className="dv2-ach__name">
                            {item.achievementName || 'Achievement'}
                        </span>
                        {achieved ? (
                            <span className="dv2-ach__meta">
                                <em>Earned</em>
                                {item.completionDate
                                    ? ` · ${parseAchievementDate(
                                          item.completionDate,
                                          true
                                      )}`
                                    : ''}
                            </span>
                        ) : (
                            <span className="dv2-ach__meta dv2-ach__meta--req">
                                {req}
                            </span>
                        )}
                    </span>
                </button>
            </li>
        );
    };

    const renderRecentChip = (item, index) => {
        if (!item) return null;
        const family = achievementFamily(item);
        const earnedLabel = item.completionDate
            ? `Earned · ${parseAchievementDate(item.completionDate, true)}`
            : 'Earned';
        const itemKey = `${family}-${item.id ?? index}`;
        const isPreview =
            recentPreview &&
            achievementFamily(recentPreview) === family &&
            (recentPreview.id ?? null) === (item.id ?? null) &&
            recentPreview.achievementName === item.achievementName;

        return (
            <li
                key={`recent-${itemKey}`}
                className={`dv2-ach__recent-item${
                    isPreview ? ' is-preview' : ''
                }`}
                style={{ ['--i']: index }}
            >
                <button
                    type="button"
                    className="dv2-ach__recent-chip"
                    aria-pressed={isNarrow ? Boolean(isPreview) : undefined}
                    onClick={() => {
                        if (isNarrow) {
                            setRecentPreview((prev) =>
                                prev &&
                                (prev.id ?? null) === (item.id ?? null) &&
                                prev.achievementName === item.achievementName
                                    ? null
                                    : item
                            );
                            return;
                        }
                        setSelected(item);
                    }}
                    aria-label={`${item.achievementName || 'Achievement'}, ${earnedLabel}`}
                >
                    <AchievementMark
                        item={item}
                        tabHint={family}
                        goalsCompleted={
                            item.achievementValue || goalsCompleted
                        }
                        size={isNarrow ? 48 : 64}
                        earned
                    />
                    <span className="dv2-ach__copy">
                        <span className="dv2-ach__name">
                            {item.achievementName || 'Achievement'}
                        </span>
                        <span className="dv2-ach__meta">
                            <em>Earned</em>
                            {item.completionDate
                                ? ` · ${parseAchievementDate(
                                      item.completionDate,
                                      true
                                  )}`
                                : ''}
                        </span>
                    </span>
                </button>
            </li>
        );
    };

    const selectedFamily = selected
        ? achievementFamily(selected, tab)
        : 'badge';
    const selectedReq = selected
        ? achievementRequirement(selected, selectedFamily)
        : '';

    return (
        <div className="dv2-ach">
            <div className="dv2-ach__overview">
            <header className="dv2-ach__header">
                <div>
                    <h2 className="dv2-ach__title">My Achievements</h2>
                    <p className="dv2-ach__subtitle">
                        {loading
                            ? 'Badges, milestones, and streaks from your Oola journey'
                            : `${earnedTotal} earned`}
                    </p>
                </div>
            </header>

            {!loading && !errorMsg ? (
                <ul className="dv2-ach__stats">
                    {stats.map((s) => (
                        <li key={s.id}>
                            <div
                                className={`dv2-ach__stat dv2-ach__stat--${s.id}${
                                    tab === s.id ? ' is-active' : ''
                                }${
                                    tab === s.id && showAll ? ' is-all' : ''
                                }`}
                            >
                                <button
                                    type="button"
                                    className="dv2-ach__stat-main"
                                    onClick={() => selectCategory(s.id, false)}
                                    aria-pressed={tab === s.id && !showAll}
                                >
                                    <span>{s.label}</span>
                                    <strong>
                                        {s.earned}
                                        <em> / {s.total || 0}</em>
                                    </strong>
                                </button>
                                <button
                                    type="button"
                                    className="dv2-ach__stat-viewall"
                                    onClick={() => selectCategory(s.id, true)}
                                    aria-pressed={tab === s.id && showAll}
                                >
                                    View all →
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            ) : null}

            {nextGoal && !loading ? (
                <section className="dv2-ach__next" aria-label="Next achievement">
                    <h3 className="dv2-ach__section-title">Next achievement</h3>
                    <button
                        type="button"
                        className="dv2-ach__next-card"
                        onClick={() => setSelected(nextGoal.item)}
                    >
                        <AchievementMark
                            item={nextGoal.item}
                            tabHint={nextGoal.family}
                            goalsCompleted={
                                nextGoal.item.achievementValue ||
                                goalsCompleted
                            }
                            size={72}
                            earned={false}
                        />
                        <div className="dv2-ach__next-copy">
                            <strong>{nextGoal.label}</strong>
                            <span className="dv2-ach__next-frac">
                                {nextGoal.current} / {nextGoal.target}
                            </span>
                            <span
                                className="dv2-ach__next-bar"
                                aria-hidden
                            >
                                <i
                                    style={{
                                        width: `${Math.min(
                                            100,
                                            (nextGoal.current / nextGoal.target) *
                                                100
                                        )}%`,
                                    }}
                                />
                            </span>
                            <em>
                                {nextGoal.remaining} {nextGoal.unit} to your next{' '}
                                {nextGoal.family}
                            </em>
                        </div>
                    </button>
                </section>
            ) : null}
            </div>

            <div className="dv2-ach__catalog">
            {recent.length > 0 ? (
                <section className="dv2-ach__recent" aria-label="Recently earned">
                    <h3 className="dv2-ach__section-title dv2-ach__section-title--major">
                        Recently earned
                    </h3>
                    <ul className="dv2-ach__recent-stack">
                        {recent.map((item, index) =>
                            renderRecentChip(item, index)
                        )}
                    </ul>
                    {isNarrow && recentPreview ? (
                        <div
                            className="dv2-ach__recent-detail"
                            role="status"
                        >
                            <p className="dv2-ach__recent-detail-name">
                                {recentPreview.achievementName || 'Achievement'}
                            </p>
                            <p className="dv2-ach__recent-detail-meta">
                                Earned
                                {recentPreview.completionDate
                                    ? ` · ${parseAchievementDate(
                                          recentPreview.completionDate,
                                          true
                                      )}`
                                    : ''}
                            </p>
                        </div>
                    ) : null}
                </section>
            ) : null}

            <nav className="dv2-ach__tabs" aria-label="Achievement categories">
                {TABS.map((t) => {
                    const s = stats.find((x) => x.id === t.id);
                    return (
                        <button
                            key={t.id}
                            type="button"
                            className={`dv2-ach__tab${
                                tab === t.id ? ' is-active' : ''
                            }`}
                            onClick={() => selectCategory(t.id, false)}
                        >
                            {t.label}
                            <em>
                                {s ? `${s.earned} / ${s.total}` : 0}
                            </em>
                        </button>
                    );
                })}
            </nav>

            {loading ? (
                <p className="dv2-ach__status">Loading achievements…</p>
            ) : null}
            {errorMsg ? <p className="dv2-ach__error">{errorMsg}</p> : null}

            {!loading && !errorMsg && list.length === 0 ? (
                <p className="dv2-ach__status">
                    {showAll
                        ? `No ${tab} yet.`
                        : `No earned ${tab} yet.`}
                </p>
            ) : null}

            {!loading && list.length > 0 ? (
                <ul className="dv2-ach__grid">
                    {list.map((item, index) => renderCard(item, index, false))}
                </ul>
            ) : null}
            </div>

            {selected
                ? ReactDOM.createPortal(
                      <div
                          className="dv2-ach__modal"
                          role="presentation"
                          onClick={closeDetail}
                      >
                          <div
                              className="dv2-ach__modal-card"
                              role="dialog"
                              aria-modal="true"
                              aria-labelledby="dv2-ach-modal-title"
                              onClick={(e) => e.stopPropagation()}
                          >
                              <button
                                  type="button"
                                  className="dv2-ach__modal-close"
                                  aria-label="Close"
                                  onClick={closeDetail}
                              >
                                  ×
                              </button>
                              <AchievementMark
                                  item={selected}
                                  tabHint={selectedFamily}
                                  goalsCompleted={
                                      selected.achievementValue ||
                                      goalsCompleted
                                  }
                                  size={128}
                                  earned={Boolean(selected.achieved)}
                              />
                              <h3
                                  id="dv2-ach-modal-title"
                                  className="dv2-ach__modal-title"
                              >
                                  {selected.achievementName || 'Achievement'}
                              </h3>
                              {selected.achieved ? (
                                  <p className="dv2-ach__modal-date">
                                      Earned{' '}
                                      {parseAchievementDate(
                                          selected.completionDate
                                      ) || 'recently'}
                                  </p>
                              ) : (
                                  <p className="dv2-ach__modal-req">
                                      {selectedReq}
                                  </p>
                              )}
                              {selected.achieved && selected.congratsMessage ? (
                                  <p className="dv2-ach__modal-msg">
                                      {selected.congratsMessage}
                                  </p>
                              ) : null}
                              {!selected.achieved ? (
                                  <p className="dv2-ach__modal-msg">
                                      Keep going. This stays visible so you know
                                      what you’re working toward.
                                  </p>
                              ) : null}
                              <div className="dv2-ach__modal-actions">
                                  <button
                                      type="button"
                                      className="dv2-ach__modal-btn"
                                      onClick={closeDetail}
                                  >
                                      Close
                                  </button>
                              </div>
                          </div>
                      </div>,
                      document.body
                  )
                : null}
        </div>
    );
};

export default AchievementsPanel;
